import { clientConfig, getSdk } from "@quickpoint.me/shared";
import { getAuth, getIdToken } from "firebase/auth";
import { GraphQLClient } from "graphql-request";
import { useState } from "react";
import {
	useRecoilValue,
	useRecoilValueLoadable,
	useSetRecoilState,
} from "recoil";
import { utilityFileNameMessage } from "../lib/codeMessages";
import { Authorization } from "../state/authAtoms";
import { OwnerName } from "../state/authSelectors";
import { PublishAtom } from "../state/publish";
import { StackCfKeyAtom } from "../state/stackAtoms";
import { StackFileNameSelectorFamily } from "../state/stackSelectors";

/**
 * Publish the current story. Safe in components without a Suspense
 * boundary: the async OwnerName selector is read as a loadable, and
 * PublishAtom is only written, never read.
 */
export function usePublishStack() {
	const authorization = useRecoilValue(Authorization);
	const ownerNameLoadable = useRecoilValueLoadable(OwnerName);
	const stackCfKey = useRecoilValue(StackCfKeyAtom);
	const stackFileName = useRecoilValue(StackFileNameSelectorFamily(stackCfKey));
	const setPublished = useSetRecoilState(PublishAtom);
	const [publishing, setPublishing] = useState(false);

	const ownerReady =
		ownerNameLoadable.state === "hasValue" && !!ownerNameLoadable.contents;

	const canPublish =
		authorization.isLoggedIn &&
		ownerReady &&
		!!stackCfKey &&
		!(stackFileName in utilityFileNameMessage);

	const publishStack = async () => {
		if (!canPublish || publishing) return;
		try {
			const user = getAuth().currentUser;
			if (!user) return;
			const jwt = await getIdToken(user);

			setPublishing(true);
			const client = new GraphQLClient(clientConfig.gqlWriteStack());
			const sdk = getSdk(client);
			await sdk.PublishStack({ stackCfKey, jwt });
			setPublished(true);
		} catch (error) {
			console.error("publishStack error:", error);
		} finally {
			setPublishing(false);
		}
	};

	return { publishing, canPublish, publishStack };
}
